import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import SEO from '@/components/layout/SEO';
import { CONCEPTS } from '@/data/concepts';
import { MaterialIcon } from '@/components/ui/MaterialIcon';

const ACCENT_CYCLE = ['#8cacff', '#00cffc', '#d674ff'] as const;

export default function KnowledgeVaultPage() {
    const concepts = useMemo(
        () => Object.values(CONCEPTS).sort((left, right) => left.title.localeCompare(right.title)),
        [],
    );

    const spotlightConcepts = concepts.slice(0, 6);
    const archiveColumns = [concepts.slice(0, 10), concepts.slice(10, 20), concepts.slice(20)];

    return (
        <div className="min-h-screen bg-[#0e0e0e] px-4 pb-24 pt-10 text-[#e8eaf0] sm:px-6 lg:px-8">
            <SEO
                title="STARTZONE.DEV | KNOWLEDGE_VAULT"
                description="Browse the canonical concept archive for every system, quest, and deployment lane inside startzone.dev."
                canonicalUrl="https://startzone.dev/concepts"
            />

            <div className="mx-auto flex max-w-7xl gap-6">
                <aside className="sticky top-28 hidden h-fit w-72 shrink-0 space-y-8 lg:block">
                    <div className="bg-[#131313] p-6 shadow-[inset_2px_0_0_0_rgba(0,207,252,0.35)]">
                        <div className="mb-4 flex items-center gap-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#00cffc]">
                            <span className="h-2 w-2 rounded-full bg-[#00cffc]" />
                            Vault Protocol
                        </div>
                        <div className="space-y-2 font-['JetBrains_Mono'] text-[11px] uppercase tracking-[0.18em] text-[#767575]">
                            <div className="flex items-center justify-between">
                                <span>INDEX_STATE</span>
                                <span className="text-[#00cffc]">SYNCED</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>ARCHIVE_NODES</span>
                                <span className="text-white">{concepts.length.toString().padStart(2, '0')}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span>ACCESS_TIER</span>
                                <span className="text-[#8cacff]">ARMORY-04</span>
                            </div>
                        </div>
                    </div>

                    <div className="bg-[#131313] p-6 shadow-[inset_2px_0_0_0_rgba(140,172,255,0.35)]">
                        <h2 className="mb-5 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.28em] text-[#8cacff]">
                            Archive Spectrum
                        </h2>
                        <div className="space-y-4">
                            {[
                                ['PRIMARY_CATALOG', 'Foundation protocols, command logic, and infra basics.'],
                                ['LIVE_INTEL', 'Recently surfaced concepts from active quest modules.'],
                                ['DEEP_ARCHIVE', 'Advanced nodes queued for higher-tier deployments.'],
                            ].map(([label, copy], index) => (
                                <div key={label} className="bg-[#1f2020] p-4">
                                    <div className="mb-2 flex items-center gap-2">
                                        <span
                                            className="h-2 w-2"
                                            style={{ backgroundColor: ACCENT_CYCLE[index] }}
                                        />
                                        <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-white">
                                            {label}
                                        </span>
                                    </div>
                                    <p className="text-xs leading-relaxed text-[#acabaa]">{copy}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-[#131313] p-6">
                        <div className="mb-3 flex items-center justify-between font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575]">
                            <span>Archive Integrity</span>
                            <span className="text-[#00cffc]">96%</span>
                        </div>
                        <div className="h-1 bg-[#252626]">
                            <div className="h-full w-[96%] bg-[#00cffc] shadow-[0_0_10px_rgba(0,207,252,0.45)]" />
                        </div>
                    </div>
                </aside>

                <main className="min-w-0 flex-1">
                    <section className="mb-12 grid gap-8 xl:grid-cols-[minmax(0,1fr)_18rem]">
                        <div className="bg-[#131313] px-6 py-8 shadow-[inset_4px_0_0_0_rgba(0,207,252,0.6)] sm:px-8">
                            <p className="mb-4 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.35em] text-[#00cffc]">
                                [ARMORY_INDEX]
                            </p>
                            <h1 className="font-['Space_Grotesk'] text-5xl font-black uppercase tracking-[-0.06em] text-white md:text-7xl">
                                Knowledge <span className="text-[#00cffc]">Vault</span>
                            </h1>
                            <p className="mt-6 max-w-2xl text-sm leading-7 text-[#acabaa] md:text-base">
                                Every concept in STARTZONE.DEV routes through this archive first. Use it as the source-of-truth layer
                                for your quests, system briefings, and tactical refreshers.
                            </p>
                        </div>

                        <div className="grid gap-4 sm:grid-cols-3 xl:grid-cols-1">
                            {[
                                ['CONCEPTS_INDEXED', String(concepts.length).padStart(2, '0'), '#8cacff'],
                                ['SPOTLIGHT_NODES', '06', '#00cffc'],
                                ['ARCHIVE_STATUS', 'LIVE', '#d674ff'],
                            ].map(([label, value, color]) => (
                                <div
                                    key={label}
                                    className="bg-[#131313] px-5 py-6"
                                    style={{ boxShadow: `inset 2px 0 0 0 ${color}55` }}
                                >
                                    <div className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575]">
                                        {label}
                                    </div>
                                    <div className="mt-2 font-['Space_Grotesk'] text-3xl font-black uppercase text-white">
                                        <span style={{ color }}>{value}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    <section className="mb-14">
                        <div className="mb-8 flex items-end justify-between gap-4">
                            <div>
                                <p className="mb-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#8cacff]">
                                    PRIME_ARCHIVE // 06
                                </p>
                                <h2 className="font-['Space_Grotesk'] text-3xl font-black uppercase tracking-tight text-white">
                                    Featured Intel Packets
                                </h2>
                            </div>
                            <div className="hidden font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575] md:block">
                                FULL_SYNC_ACTIVE
                            </div>
                        </div>

                        <div className="grid gap-5 xl:grid-cols-3">
                            {spotlightConcepts.map((concept, index) => {
                                const accent = ACCENT_CYCLE[index % ACCENT_CYCLE.length];

                                return (
                                    <Link
                                        key={concept.id}
                                        to={`/concepts/${concept.id}`}
                                        className="group relative overflow-hidden bg-[#131313] p-6 transition-colors duration-200 hover:bg-[#1f2020]"
                                        style={{ boxShadow: `inset 2px 0 0 0 ${accent}` }}
                                    >
                                        <div
                                            className="pointer-events-none absolute inset-0 opacity-[0.04]"
                                            style={{
                                                backgroundImage:
                                                    'repeating-linear-gradient(to bottom, rgba(255,255,255,0.1) 0, rgba(255,255,255,0.1) 1px, transparent 1px, transparent 4px)',
                                            }}
                                        />
                                        <div className="relative">
                                            <div className="mb-5 flex items-start justify-between gap-3">
                                                <span
                                                    className="bg-[#252626] px-2 py-1 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.2em]"
                                                    style={{ color: accent }}
                                                >
                                                    {concept.id.slice(0, 10).toUpperCase()}
                                                </span>
                                                <MaterialIcon icon="library_books" size={18} className="text-[#767575] transition-colors group-hover:text-white" />
                                            </div>
                                            <h3 className="mb-3 font-['Space_Grotesk'] text-2xl font-black uppercase tracking-tight text-white">
                                                {concept.title}
                                            </h3>
                                            <p className="min-h-[4.5rem] text-sm leading-6 text-[#acabaa]">
                                                {concept.summary}
                                            </p>
                                            <div className="mt-5 flex flex-wrap gap-2">
                                                {concept.tags.slice(0, 3).map((tag) => (
                                                    <span
                                                        key={tag}
                                                        className="bg-[#252626] px-2 py-1 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#767575]"
                                                    >
                                                        {tag}
                                                    </span>
                                                ))}
                                            </div>
                                            <div className="mt-6 flex items-center justify-between border-t border-white/5 pt-4">
                                                <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575]">
                                                    Open Briefing
                                                </span>
                                                <span
                                                    className="inline-flex items-center gap-1 font-['Space_Grotesk'] text-[10px] font-black uppercase tracking-[0.22em]"
                                                    style={{ color: accent }}
                                                >
                                                    DEPLOY
                                                    <MaterialIcon icon="arrow_forward" size={12} className="text-current" />
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                );
                            })}
                        </div>
                    </section>

                    <section className="mb-14 bg-[#1f2020] px-6 py-8 sm:px-8">
                        <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_14rem] xl:items-center">
                            <div>
                                <p className="mb-3 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.28em] text-[#d674ff]">
                                    REQUEST_NEW_ARCHIVE
                                </p>
                                <h2 className="font-['Space_Grotesk'] text-3xl font-black uppercase tracking-tight text-white">
                                    Missing a concept packet?
                                </h2>
                                <p className="mt-4 max-w-2xl text-sm leading-7 text-[#acabaa]">
                                    If a concept is absent from the vault, open a manual sync request and the archive team will queue
                                    a new intel packet for indexing.
                                </p>
                            </div>
                            <div className="flex justify-start xl:justify-end">
                                <Link
                                    to="/contact"
                                    className="inline-flex items-center gap-3 bg-gradient-to-r from-[#8cacff] to-[#769dff] px-6 py-4 font-['Space_Grotesk'] text-xs font-black uppercase tracking-[0.22em] text-[#001f55] transition-all hover:brightness-110 active:scale-[0.99]"
                                >
                                    REQUEST SYNC
                                    <MaterialIcon icon="arrow_forward" size={14} className="text-current" />
                                </Link>
                            </div>
                        </div>
                    </section>

                    <section>
                        <div className="mb-8 flex items-end justify-between gap-4">
                            <div>
                                <p className="mb-2 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.3em] text-[#8cacff]">
                                    FULL_INDEX
                                </p>
                                <h2 className="font-['Space_Grotesk'] text-3xl font-black uppercase tracking-tight text-white">
                                    Archive Directory
                                </h2>
                            </div>
                            <span className="font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575]">
                                {concepts.length} active entries
                            </span>
                        </div>

                        <div className="grid gap-4 xl:grid-cols-3">
                            {archiveColumns.map((column, columnIndex) => (
                                <div key={columnIndex} className="bg-[#131313] p-5">
                                    <div className="mb-4 font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.22em] text-[#767575]">
                                        Segment {String(columnIndex + 1).padStart(2, '0')}
                                    </div>
                                    <div className="space-y-2">
                                        {column.map((concept) => (
                                            <Link
                                                key={concept.id}
                                                to={`/concepts/${concept.id}`}
                                                className="flex items-center justify-between bg-[#1f2020] px-4 py-3 transition-colors hover:bg-[#252626]"
                                            >
                                                <div className="min-w-0">
                                                    <div className="truncate font-['Space_Grotesk'] text-sm font-black uppercase tracking-tight text-white">
                                                        {concept.title}
                                                    </div>
                                                    <div className="truncate font-['JetBrains_Mono'] text-[10px] uppercase tracking-[0.18em] text-[#767575]">
                                                        {concept.id}
                                                    </div>
                                                </div>
                                                <MaterialIcon icon="chevron_right" size={16} className="text-[#8cacff]" />
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}
